import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { cn } from '@/lib/utils';
import { GUIDE_HREF_PREFIX } from '@/help/markdown';

export interface MarkdownArticleProps {
  readonly markdown: string;
  readonly className?: string;
  /** Called with the guide id when a `guide:` link is clicked. Without it those links render as plain text. */
  readonly onGuide?: (guideId: string) => void;
  /** Whether the reader may run a tour. A link to one they may not run reads as plain text. */
  readonly canRunGuide?: (guideId: string) => boolean;
}

/** Unwraps `guide:` links the reader may not run, keeping their words. */
export function withoutUnavailableGuides(html: string, canRunGuide: (guideId: string) => boolean): string {
  return html.replace(/<a href="#guide:([^"]+)">([\s\S]*?)<\/a>/g, (link: string, id: string, text: string) => (canRunGuide(id) ? link : text));
}

/**
 * Markdown from the repo, rendered with the portal's prose typography.
 * In-app links navigate without a reload; guide links become buttons.
 */
export function MarkdownArticle({ markdown, className, onGuide, canRunGuide }: MarkdownArticleProps): React.ReactElement {
  const navigate = useNavigate();
  const html = useMemo(() => {
    const raw = marked.parse(markdown, { async: false, gfm: true, breaks: false }) as string;
    const clean = DOMPurify.sanitize(raw, { ADD_ATTR: ['target'] });
    return canRunGuide ? withoutUnavailableGuides(clean, canRunGuide) : clean;
  }, [markdown, canRunGuide]);

  return (
    <div
      className={cn('portal-prose help-article', className)}
      // Sanitized above; the source is our own repo, never user input.
      dangerouslySetInnerHTML={{ __html: html }}
      onClick={(event) => {
        const anchor = (event.target as HTMLElement).closest('a');
        if (!anchor) return;
        const href = anchor.getAttribute('href') ?? '';
        if (href.startsWith(GUIDE_HREF_PREFIX)) {
          event.preventDefault();
          onGuide?.(href.slice(GUIDE_HREF_PREFIX.length));
          return;
        }
        if (href.startsWith('/') && !anchor.target) {
          event.preventDefault();
          navigate(href);
        }
      }}
    />
  );
}
